import React, { useState, useEffect } from "react";
import { Box, Typography, TextField, Button, CircularProgress, IconButton, List, Toolbar, Dialog } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { toast } from "react-toastify";

import { ApiPost, ApiPut } from "../../helpers/API/ApiData";

import { validateforUserData } from "./validateforUserData";

type Props = {
  isEditMode: boolean;
  idForSurveyEdit: string;
  inputValue: any;
  editSurveyModalOpen: boolean;
  setEditMode: React.Dispatch<React.SetStateAction<boolean>>;
  setInputValue: React.Dispatch<any>;
  handleSurveyModalClose: () => void;
  setEditSurveyModalOpen: (value: React.SetStateAction<boolean>) => void;
  getSurveyDetails: () => Promise<void>;
};

const optionData = ["star", "text", "option"];
const optionDataType = ["DIE KEB COMMUNITY", "DEIN IMPACT ECK"];

export const EditSurveyModal = ({
  isEditMode,
  inputValue,
  idForSurveyEdit,
  editSurveyModalOpen,
  setEditMode,
  setInputValue,
  handleSurveyModalClose,
  setEditSurveyModalOpen,
  getSurveyDetails,
}: Props) => {
  const [errors, setErrors] = useState<any>({});
  const [loading, setLoading] = useState(false);

  const [surveyOptions, setSurveyOptions] = useState<
    Array<{ option: string; option_ger: string }>
  >([]);

  useEffect(() => {
    if (isEditMode && inputValue.surveyOption && inputValue.surveyOption_ger) {
      const initialOptions = inputValue.surveyOption.map(
        (option: string, index: number) => ({
          option,
          option_ger: inputValue.surveyOption_ger[index],
        })
      );
      setSurveyOptions(initialOptions);
    } else {
      setSurveyOptions([{ option: "", option_ger: "" }]);
    }
  }, [isEditMode, inputValue.surveyOption, inputValue.surveyOption_ger]);

  const handleOnChange = (e: any, index?: number, isGerman?: boolean) => {
    const { name, value } = e.target;
    if (typeof index === "number") {
      const updatedOptions = [...surveyOptions];
      if (isGerman) {
        updatedOptions[index].option_ger = value;
      } else {
        updatedOptions[index].option = value;
      }
      setSurveyOptions(updatedOptions);
    } else {
      setInputValue({ ...inputValue, [name]: value });
      setErrors({ ...errors, [name]: "" });
    }
  };

  const handleAddOption = () => {
    setSurveyOptions([...surveyOptions, { option: "", option_ger: "" }]);
  };

  const handleRemoveOption = (index: number) => {
    const updatedOptions = [...surveyOptions];
    updatedOptions.splice(index, 1);
    setSurveyOptions(updatedOptions);
  };

  const saveSurveyDetails = () => {
    if (!validateforUserData({ inputValue, setErrors })) return;

    setLoading(true);

    const data = {
      ...inputValue,
      surveyOption: surveyOptions.map((opt) => opt.option),
      surveyOption_ger: surveyOptions.map((opt) => opt.option_ger),
    };

    const apiCall = isEditMode
      ? ApiPut(`survey/update?id=${idForSurveyEdit}`, data)
      : ApiPost("survey/create", data);

    apiCall
      .then((res) => {
        isEditMode && setEditMode(false);
        setEditSurveyModalOpen(false);
        setLoading(false);
        toast.success(
          `Umfragen ${isEditMode ? "aktualisiert" : "erfolgreich hinzugefügt"}`
        );
        getSurveyDetails();
      })
      .catch((err: any) => {
        console.error("saveSurveyDetails | err", err);
        setLoading(false);
        toast.error(err?.response?.data?.message || "Etwas ist schief gelaufen. Bitte versuche es erneut");
      });
  };

  return (
    <Dialog
      fullScreen
      open={editSurveyModalOpen}
      onClose={handleSurveyModalClose}
    >
      <Toolbar>
        <IconButton
          edge="end"
          color="inherit"
          onClick={handleSurveyModalClose}
          aria-label="close"
          sx={{ ml: "auto" }}
        >
          <CloseIcon />
        </IconButton>
      </Toolbar>
      <List>
        <Box sx={{ maxWidth: 1000, mx: "auto", p: 3 }}>
          <Box sx={{ display: "flex", mb: 3, flexWrap: { xs: "wrap", lg: "nowrap" } }}>
            <Typography sx={{ width: { xs: "100%", lg: "16.67%" }, pt: 1 }}></Typography>
            <Typography variant="h6" sx={{ width: { xs: "100%", lg: "41.67%" }, textAlign: "center" }}>German</Typography>
            <Typography variant="h6" sx={{ width: { xs: "100%", lg: "41.67%" }, textAlign: "center" }}>English</Typography>
          </Box>
          <Box sx={{ display: "flex", mb: 3, flexWrap: { xs: "wrap", lg: "nowrap" } }}>
            <Typography sx={{ width: { xs: "100%", lg: "16.67%" }, pt: 1 }}>
              Fragestellung
            </Typography>
            <Box sx={{ width: { xs: "100%", lg: "41.67%" }, px: 1 }}>
              <TextField
                type="text"
                size="small"
                fullWidth
                variant="outlined"
                id="surveyQuestion_ger"
                name="surveyQuestion_ger"
                value={inputValue?.surveyQuestion_ger}
                onChange={(e) => {
                  handleOnChange(e);
                }}
              />
              <Typography variant="caption" color="error">{errors?.surveyQuestion_ger}</Typography>
            </Box>
            <Box sx={{ width: { xs: "100%", lg: "41.67%" }, px: 1 }}>
              <TextField
                type="text"
                size="small"
                fullWidth
                variant="outlined"
                id="surveyQuestion"
                name="surveyQuestion"
                value={inputValue?.surveyQuestion}
                onChange={(e) => {
                  handleOnChange(e);
                }}
              />
              <Typography variant="caption" color="error">{errors?.surveyQuestion}</Typography>
            </Box>
          </Box>
          <Box sx={{ display: "flex", mb: 3, flexWrap: { xs: "wrap", lg: "nowrap" } }}>
            <Typography sx={{ width: { xs: "100%", lg: "16.67%" }, pt: 1 }}>
              Art der Frage
            </Typography>
            <Box sx={{ width: { xs: "100%", lg: "83.33%" } }}>
              <TextField
                select
                size="small"
                fullWidth
                variant="outlined"
                name="surveyType"
                id="surveyType"
                value={inputValue?.surveyType || ""}
                onChange={(e) => handleOnChange(e)}
                SelectProps={{ native: true }}
              >
                <option value="" disabled>
                  Wählen
                </option>
                {optionData?.map((data: any, index: number) => {
                  return (
                    <option key={index} value={data}>
                      {data}
                    </option>
                  );
                })}
              </TextField>
              <Typography variant="caption" color="error">{errors?.surveyType}</Typography>
            </Box>
          </Box>
          {inputValue?.surveyType === "option" && (
            <Box sx={{ display: "flex", mb: 3, flexWrap: { xs: "wrap", lg: "nowrap" } }}>
              <Typography sx={{ width: { xs: "100%", lg: "16.67%" }, pt: 1 }}>
                Umfrage-Möglichkeit
              </Typography>
              <Box sx={{ width: { xs: "100%", lg: "83.33%" } }}>
                {surveyOptions.map((option, index) => (
                  <Box key={index} sx={{ display: "flex", alignItems: "center", mb: 3, gap: 2 }}>
                    <Box sx={{ flex: 5 }}>
                      <TextField
                        type="text"
                        size="small"
                        fullWidth
                        variant="outlined"
                        placeholder="Option DE"
                        value={option.option_ger}
                        onChange={(e) => handleOnChange(e, index, true)}
                      />
                    </Box>
                    <Box sx={{ flex: 5 }}>
                      <TextField
                        type="text"
                        size="small"
                        fullWidth
                        variant="outlined"
                        placeholder="Option EN"
                        value={option.option}
                        onChange={(e) => handleOnChange(e, index, false)}
                      />
                    </Box>
                    <Box sx={{ flex: 2 }}>
                      <Button
                        variant="outlined"
                        color="error"
                        onClick={() => handleRemoveOption(index)}
                      >
                        -
                      </Button>
                    </Box>
                  </Box>
                ))}
                <Box sx={{ display: "flex" }}>
                  <Box sx={{ flex: 1 }}>
                    <Button
                      variant="outlined"
                      color="success"
                      onClick={handleAddOption}
                    >
                      +
                    </Button>
                  </Box>
                </Box>
              </Box>
            </Box>
          )}

          <Box sx={{ display: "flex", mb: 3, flexWrap: { xs: "wrap", lg: "nowrap" } }}>
            <Typography sx={{ width: { xs: "100%", lg: "16.67%" }, pt: 1 }}>Umfragen</Typography>
            <Box sx={{ width: { xs: "100%", lg: "83.33%" } }}>
              <TextField
                select
                size="small"
                fullWidth
                variant="outlined"
                name="surveyField"
                id="surveyField"
                value={inputValue?.surveyField || ""}
                onChange={(e) => handleOnChange(e)}
                SelectProps={{ native: true }}
              >
                <option value="" disabled>
                  Umfrage auswählen
                </option>
                {optionDataType?.map((data: any) => {
                  return <option key={data} value={data}>{data}</option>;
                })}
              </TextField>
              <Typography variant="caption" color="error">{errors?.surveyField}</Typography>
            </Box>
          </Box>
          <Box sx={{ display: "flex", mb: 3, flexWrap: { xs: "wrap", lg: "nowrap" } }}>
            <Typography sx={{ width: { xs: "100%", lg: "16.67%" }, pt: 1 }}>
              Punktzahl
            </Typography>
            <Box sx={{ width: { xs: "100%", lg: "83.33%" } }}>
              <TextField
                type="number"
                size="small"
                fullWidth
                variant="outlined"
                id="coin"
                name="coin"
                value={inputValue?.coin}
                onChange={(e) => {
                  handleOnChange(e);
                }}
              />
              <Typography
                variant="caption"
                color="error"
              >
                {errors?.coin}
              </Typography>
            </Box>
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
            {loading ? (
              <Button variant="contained" sx={{ mr: 2 }} disabled>
                <span>Hinzufügen</span>
                <CircularProgress size={20} sx={{ mx: 1, color: "white" }} />
              </Button>
            ) : (
              <Button
                variant="contained"
                sx={{ mr: 2 }}
                onClick={() => {
                  if (
                    inputValue.surveyType === "option" &&
                    surveyOptions.every(
                      (option) =>
                        option.option.trim() && option.option_ger.trim()
                    )
                  ) {
                    saveSurveyDetails();
                  } else if (["star", "text"].includes(inputValue.surveyType)) {
                    saveSurveyDetails();
                  } else {
                    toast.error("Bitte füllen Sie alle Felder aus");
                  }
                }}
              >
                <span>{isEditMode ? "Aktualisieren" : "Hinzufügen"}</span>
                {loading && (
                  <CircularProgress size={20} sx={{ mx: 1, color: "white" }} />
                )}
              </Button>
            )}
          </Box>
        </Box>
      </List>
    </Dialog>
  );
};
